import { StoreApi } from 'zustand';

import { TTextLocalized } from '@northladder/utilities';

import { IDateRangeType } from '../../types';

/**
 * Rank enums for selecting and triggering the api for particular rank
 * - This determines what search filter is applied.
 * - See also `TBidRankStatus`.
 */
export type TBidRank =
  | 'ALL_DEALS'
  | 'BID_IN_SUMMIT' // winning
  | 'BID_QUANTITY_ZERO' // out-of-qty
  | 'BID_OUT_OF_SUMMIT' // not-winning
  | 'NO_BIDS' // no-bids
  | 'HOT_DEAL' // hot-deal
  | 'MY_BIDS';

/**
 * `IBrand` defines the structure of brand filter options.
 */
export interface IBrand {
  id: string;
  name: TTextLocalized;
  nameStr: string;
}

export const bidRankStatusesConfig = [
  'hot-deal',
  'rank-one',
  'winning',
  'not-winning',
  'out-of-summit',
  'no-bids',
  'my-bids',
  'out-of-qty',
] as const;

export enum EBidRankStatus {
  HOT_DEAL = 'hot-deal',
  MY_BIDS = 'my-bids',
  RANK_ONE = 'rank-one',
  WINNING = 'winning',
  NOT_WINNING = 'not-winning',
  OUT_OF_SUMMIT = 'out-of-summit',
  NO_BIDS = 'no-bids',
  OUT_OF_QTY = 'out-of-qty',
}

/**
 * Rank enums for selecting and triggering the api for particular rank
 * - This determines what badge gets displayed for each rank.
 * - See also `TBidRank` above.
 */
export type TBidRankStatus = (typeof bidRankStatusesConfig)[number];

export type TRankFilter =
  | 'outOfQuantity'
  | 'bidNotWinning'
  | 'winningRange'
  | 'noBids'
  | 'myBids';

export interface IRankFilterItem {
  filter: TRankFilter;
  isSelected: boolean;
  rankId: TBidRank;
  status: TBidRankStatus;
  /**
   * Bid rank for display. Maps to status for backward compatibility.
   */
  rank?: TBidTag;
}

/**
 * `ICategoryItem` defines the structure of category filter options.
 */
export interface ICategoryItem {
  bidCount: number;
  brands: IBrand[];
  categoryName: TTextLocalized;
  categoryNameStr: string;
  id: string;
  imageUrl: string;
  priority: number;
  value: string;
}

export interface IContextId {
  contextId: string;
  searchKey: string;
}

export type TSortDirection = 'DESCENDING' | 'ASCENDING';

/**
 * This provides configuration for the bids sorting options.
 */
export const sortOptions: TSortDirection[] = ['DESCENDING', 'ASCENDING'];

/**
 * Sort options enum for bids list table
 */
export enum EBidsSortOptions {
  ASSET_NAME = 'assetName',
  RANK = 'rank',
  HIGHEST_BID = 'highestBid',
  MY_BID = 'myBid',
  AVAILABLE_QUANTITY = 'availableQuantity',
  UPDATED_AT = 'updatedAt',
}

/**
 * `IMyBid` object type from bid list api
 */
export interface IMyBid {
  availableQuantity: number;
  bidId: string;
  isAutoTopupEnabled: boolean; // TODO: Why these two are different?
  isBidActive: boolean;
  isSpotSummit: boolean;
  itemCondition: string;
  maxQuantity: number;
  /**
   * @deprecated - This field is no longer used as mobile is using `rankNumber`
   */
  rank: number;
  rankNumber: number;
  spotPrice: number;
  /**
   * @deprecated - This field is no longer used as mobile is using `spotPrice`
   */
  topSpotPrice: number;
  updatedDate: number;
}

export interface ICreditLimitDetails {
  blockedLimit: number;
  blockedLimitType: string;
  creditLimit: number;
  isBlockedLimitExceeded: boolean;
  isBlockedLimitExceededDate: number;
  isExceeded: boolean;
  isExceededDate: number;
}

export const bidTagConfig = [
  'winning',
  'rank-one',
  'runner-up',
  'in-queue',
  'not-winning',
  'out-of-qty',
  'no-bids',
  'my-bids',
] as const;

export type TBidTag = (typeof bidTagConfig)[number];

/**
 * BidList api asset type, formatted from `IAssetRaw`
 */
export interface IAsset {
  assetId: string;
  assetImage: string;
  /**
   * Gets the `brandName` and `name` combined as per locale.
   */
  assetName: TTextLocalized;
  brandId: string;
  brandLogo: string | null;
  brandName: string;
  categoryName: string;
  creditLimitDetails?: ICreditLimitDetails;
  gapToFirstRank: number | null;
  icon: string;
  isHotDeal: boolean;
  lastUpdatedOn: number;
  myBid: IMyBid | null;
  myBidQuantity: IMyBid['availableQuantity'];
  name: string | null;
  /**
   * @deprecated - this doesn't make sense on the item.
   */
  page: number;
  stepperValue: number;
  /**
   * @deprecated - This is used in computing the rank status of the badges.
   * This is to be got from myBid values and computed in the api formatter.
   * Use `rank` instead.
   */
  rankStatus: TBidRankStatus;
  rank: TBidTag;
  topBidAmount: number;
  /**
   * This is the same as the `assetName.en` to be used in tabular data without
   * forcing type alias to a string.
   * - I might later replace the `assetName` since we are not using other locales.
   */
  assetNameLabel: string;
  /**
   * See also available quantity. This is brought at the root for easier reads.
   */
  myBidPrice: IMyBid['spotPrice'];
}

export interface IBrandRaw {
  name: string;
  imageUrl: string;
  description: string;
  status: boolean;
  _id: string;
  schemaId?: string;
  webImageUrl?: string;
  bidCount: number;
  description_ar?: string;
  name_ar?: string;
}

interface IBuyerAssetGroupItemLocalized {
  categoryId: string;
  checked: boolean;
  id: string;
  label: TTextLocalized;
  labelStr: string;
  value: string;
}

// IBidFilter STORE State:: ----------------------------------------------------

export interface IBidFilterState {
  /**
   * Bid Search key for search bids in all device bids tab
   */
  bidSearchKey: string;
  /**
   * Update `bidSearchKey` state
   */
  updateBidSearchKey: (key: string) => void;
  /**
   * Bid Search key for search bids in all device bids tab
   */
  contextId: IContextId | null;
  /**
   * Update `bidSearchKey` state
   */
  updateContextId: (key: IContextId | null) => void;
  /**
   * category filter list it will provide list of bid asset categories
   */
  categoryBidFilterList: ICategoryItem[];
  /**
   * for update `categoryList` state
   */
  updateCategoryBidFilterList: (categories: ICategoryItem[]) => void;
  /**
   * selected bids category filter
   */
  selectedCategoryBidFilter: ICategoryItem | null;
  /**
   * For update bids category filter
   */
  updateSelectedCategoryBidFilter: (category: ICategoryItem | null) => void;
  /**
   * Selected bids brand filter state.
   */
  selectedBrandBidFilterList: IBrand[];
  /**
   * For update Selected bids brand filter.
   */
  updateSelectedBrandBidFilterList: (brand: IBrand[]) => void;
  /**
   * Bid Search key for search bids in hot deals bids tab
   */
  hotDealsBidSearchKey: string;
  /**
   * Update `hotDealsBidSearchKey` state
   */
  updateHotDealsBidsSearchKey: (key: string) => void;
  /**
   * Selected hot deals bids category filter index state
   */
  selectedHotDealsCategoryBidFilterIndex: number;
  /**
   * For update selected hot deals bids category filter index
   */
  updateSelectedHotDealsCategoryBidFilterIndex: (category: number) => void;
  /**
   * Selected hot deals bids brand filter. (list of brand ids)
   */
  selectedHotDealsBrandIdBidFilterList: string[];
  /**
   * Update selected hot deals bids brand filter
   */
  updateSelectedHotDealsBrandIdBidFilterList: (brandId: string[]) => void;
  /**
   * Selected bids rank enum for filter state
   */
  selectedBidRankFilter?: TBidRank;
  /**
   * For update selected bids rank enum for filter state
   */
  updateBidSelectedRankFilter: (rankType?: TBidRank) => void;
  /**
   * All device bid list sort selected index state
   */
  bidListSortFilterIndex: number;
  /**
   * Update all device bid list sort selected index state
   */
  updateBidListSortFilterIndex: (sortIndex: number) => void;
  /**
   * Hot Deals bid list sort selected index state
   */
  hotDealsBidListSortIndex: number;
  /**
   * Update hot deals bid list sort selected index state
   */
  updateHotDealsBidListSortFilterIndex: (sortIndex: number) => void;
  /**
   * selected range of date
   */
  dateRange: IDateRangeType | null;
  /**
   * to update `Bids Date Range`
   */
  onUpdateBidsDateRange: (dateRange: IDateRangeType | null) => void;
  selectedBidsTabGroup: TSelectedBidTabType;
  updateSelectedBidsTabGroup: (tab: TSelectedBidTabType) => void;
  selectedAssetGroups: IBuyerAssetGroupItemLocalized[];
  updateSelectedAssetGroups: (
    assetGroups: IBuyerAssetGroupItemLocalized[]
  ) => void;
  sortBy: EBidsSortOptions | null;
  updateSortBy: (sortBy: EBidsSortOptions | null) => void;
  orderBy: TSortDirection | null;
  updateOrderBy: (orderBy: TSortDirection | null) => void;
  /**
   * Column visibility state for bids list table
   */
  bidsListTableColumnVisibility: Record<string, boolean>;
  /**
   * Update column visibility state for bids list table
   */
  updateBidsListTableColumnVisibility: (
    visibility: Record<string, boolean>
  ) => void;
  /**
   * Page size for bids list table
   */
  bidsListTablePageSize: number;
  /**
   * Update page size for bids list table
   */
  updateBidsListTablePageSize: (pageSize: number) => void;
  /**
   * Whether the filters dialog is pinned (sidebar mode)
   */
  isFiltersDialogPinned: boolean;
  /**
   * Update whether the filters dialog is pinned
   */
  onUpdateIsFiltersDialogPinned: (pinned: boolean) => void;
  /**
   * Whether the filters dialog is visible
   */
  isFiltersDialogVisible: boolean;
  /**
   * Update whether the filters dialog is visible
   */
  onUpdateIsFiltersDialogVisible: (visible: boolean) => void;
  /**
   * Column visibility state for staged bids table
   */
  stagedBidsTableColumnVisibility: Record<string, boolean>;
  /**
   * Update column visibility state for staged bids table
   */
  updateStagedBidsTableColumnVisibility: (
    visibility: Record<string, boolean>
  ) => void;
  /**
   * Page size for staged bids table
   */
  stagedBidsTablePageSize: number;
  /**
   * Update page size for staged bids table
   */
  updateStagedBidsTablePageSize: (pageSize: number) => void;
  /**
   * Column visibility state for uploaded bids table
   */
  uploadedBidsTableColumnVisibility: Record<string, boolean>;
  /**
   * Update column visibility state for uploaded bids table
   */
  updateUploadedBidsTableColumnVisibility: (
    visibility: Record<string, boolean>
  ) => void;
  /**
   * Page size for uploaded bids table
   */
  uploadedBidsTablePageSize: number;
  /**
   * Update page size for uploaded bids table
   */
  updateUploadedBidsTablePageSize: (pageSize: number) => void;
  /**
   * clears all bids filter.
   */
  clearBidsStore: () => void;
}

export type TSetBidFilters = StoreApi<IBidFilterState>['setState'];

// IBidDetails STORE State:: ---------------------------------------------------

export interface IBidLocalUpdateDetails {
  activeAssetId?: string;
  assetIndex?: number;
  pageNumber?: number;
}

/**
 * This state using to track active asset model in BidDetails
 */
export interface IBidDetailsState {
  activeAssetDetails?: IBidLocalUpdateDetails;
  updateActiveAssetDetails: (assetDetails?: IBidLocalUpdateDetails) => void;
  /**
   * Active Bid Asset details model
   * @deprecated - To be removed later as data is no longer cached on this page.
   */
  activeAssetModel?: IAsset;
  /**
   * For update Active Bid Asset details model (activeAssetModel)
   * @deprecated - To be removed later as data is no longer cached on this page.
   */
  updateActiveAssetModel: (asset?: IAsset) => void;
  /**
   * Bid updated successfully flag for showing bid update flag in BidList
   * @deprecated - To be removed later as data is no longer cached on this page.
   */
  bidUpdatedSuccessfully: boolean;
  /**
   * For update bidUpdatedSuccessfully flag
   * @deprecated - To be removed later as data is no longer cached on this page.
   */
  updateBidUpdatedSuccessfully: (flag: boolean) => void;
  /**
   * If credit limit is exceeded it will enable and it help to show blocked badge
   * on BidCard.
   */
  isCreditLimitExceeded: boolean;
  /**
   * This is for updating the state of isCreditLimitExceeded flag.
   */
  updateIsCreditLimitExceeded: (flag: boolean) => void;
}

// ISearch STORE State:: -------------------------------------------------------

/**
 * These types are used for tracking stateful logic for the search store global
 * state.
 * TODO: Get a better name for this key since search state is too generic.
 */
export interface ISearchState {
  searchKey: string;
  updateSearchKey: (key: string) => void;
}

/**
 * This type is the Bids Tabbed Overview type
 */
export type TSelectedBidTabType = 'ALL_DEALS' | 'HOT_DEALS';
